import { useEffect, useState } from "react";
import { Mail, Phone, Globe, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api } from "@/lib/api";

export const SponsorLogo = ({ s, className = "h-14", maxW = "max-w-[160px]" }) => s.logo
  ? <img src={s.logo} alt={s.name} className={`${className} w-auto ${maxW} object-contain`} />
  : <span className={`${className} inline-flex items-center px-3 font-heading font-extrabold text-gray-500 whitespace-nowrap`}>{s.name}</span>;

export const SponsorCard = ({ s, onClose }) => (
  <div data-testid={`sponsor-card-${s.id}`}>
    <div className="flex items-start justify-between gap-3">
      <div className="font-heading font-extrabold leading-snug">{s.name}</div>
      <button onClick={onClose} className="text-gray-400 hover:text-[#E60000]" aria-label="Close" data-testid="sponsor-card-close"><X className="w-4 h-4" /></button>
    </div>
    <ul className="mt-3 space-y-2 text-sm">
      {s.phone && <li><a href={`tel:${s.phone.replace(/[^\d+]/g, "")}`} className="inline-flex items-center gap-2 hover:text-[#E60000]" data-testid="sponsor-card-phone"><Phone className="w-4 h-4 text-[#E60000]" />{s.phone}</a></li>}
      {s.email && <li><a href={`mailto:${s.email}`} className="inline-flex items-center gap-2 hover:text-[#E60000] break-all" data-testid="sponsor-card-email"><Mail className="w-4 h-4 text-[#E60000] shrink-0" />{s.email}</a></li>}
      {s.website && <li><a href={s.website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 hover:text-[#E60000] break-all" data-testid="sponsor-card-website"><Globe className="w-4 h-4 text-[#E60000] shrink-0" />Visit website</a></li>}
      {!s.phone && !s.email && !s.website && <li className="text-gray-500">Contact details coming soon.</li>}
    </ul>
  </div>
);

const Item = ({ s, idx, open, setOpen }) => {
  const key = `${s.id}-${idx}`;
  return (
    <Popover open={open === key} onOpenChange={(v) => setOpen(v ? key : null)}>
      <PopoverTrigger asChild>
        <button className="shrink-0 mx-4 h-[120px] px-7 grid place-items-center bg-white border border-[#E5E7EB] rounded-lg grayscale-[40%] hover:grayscale-0 hover:border-[#E60000] transition-[filter,border-color]" title={s.name}
          aria-hidden={idx > 0} tabIndex={idx > 0 ? -1 : 0} data-testid={idx === 0 ? `sponsor-logo-${s.id}` : undefined}>
          <SponsorLogo s={s} className="h-[84px] text-xl" maxW="max-w-[240px]" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72" sideOffset={8}><SponsorCard s={s} onClose={() => setOpen(null)} /></PopoverContent>
    </Popover>
  );
};

export const SponsorsCarousel = () => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(null);
  useEffect(() => {
    api.get("/sponsors").then((r) => setItems(r.data)).catch(() => {});
  }, []);
  if (!items.length) return null;
  const copies = Math.max(2, Math.ceil(12 / items.length) * 2);
  const loop = Array.from({ length: copies }, (_, i) => i);
  return (
    <section className="bg-white border-t border-[#E5E7EB] py-10" data-testid="sponsors-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 mb-6">
        <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#E60000]">With thanks to</div>
        <h2 className="font-heading text-2xl font-extrabold tracking-tight">Our sponsors</h2>
      </div>
      <div className="sponsor-marquee overflow-hidden" data-paused={open ? "true" : "false"} data-testid="sponsors-carousel">
        <div className="sponsor-track flex w-max" style={{ animationDuration: `${items.length * copies * 3}s` }}>
          {loop.map((c) => items.map((s) => <Item key={`${s.id}-${c}`} s={s} idx={c} open={open} setOpen={setOpen} />))}
        </div>
      </div>
    </section>
  );
};
